/**
 * Firebase Authentication Context - ASCIN Supply Chain Intelligence
 * Adobe-like SSO across all Executive Force AI apps
 * Uses unified Firebase project: executive-force-ai-453011
 */

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithCustomToken,
  User as FirebaseUser 
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface User {
  name: string;
  email: string;
  role: string;
  uid?: string;
  photoURL?: string | null;
  [key: string]: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  firebaseUser: FirebaseUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Listen to Firebase auth state changes
  useEffect(() => {
    // ---- SSO bootstrap (Option C) ----
    // If the portal launched us with ?customToken=, sign in with it BEFORE
    // the auth listener resolves so the user lands authenticated.
    // Fall back to legacy ?token= behaviour (raw ID token cached locally).
    const ssoBootstrap = async () => {
      const params = new URLSearchParams(window.location.search);
      const customToken = params.get('customToken');
      const legacyToken = params.get('token');

      if (customToken) {
        console.log('🔐 ASCIN: SSO custom token detected — signing in...');
        window.history.replaceState({}, document.title, window.location.pathname);
        try {
          await signInWithCustomToken(auth, customToken);
          console.log('✅ ASCIN: signInWithCustomToken OK');
        } catch (err) {
          console.error('❌ ASCIN: signInWithCustomToken failed:', err);
        }
      } else if (legacyToken) {
        console.log('🎫 ASCIN: Legacy SSO id-token received, caching');
        localStorage.setItem('access_token', legacyToken);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    ssoBootstrap();

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        // User is signed in
        const userData: User = {
          name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
          email: fbUser.email || '',
          role: 'user', // Default role, can be fetched from custom claims
          uid: fbUser.uid,
          photoURL: fbUser.photoURL,
        };
        
        // Get custom claims for role if available
        try {
          const idTokenResult = await fbUser.getIdTokenResult();
          if (idTokenResult.claims.role) {
            userData.role = idTokenResult.claims.role as string;
          }
          if (idTokenResult.claims.organization_id) {
            userData.organization_id = idTokenResult.claims.organization_id;
          }
          // Store token for API calls
          localStorage.setItem('access_token', idTokenResult.token);
        } catch (error) {
          console.warn('Could not fetch custom claims:', error);
        }
        
        localStorage.setItem('user', JSON.stringify(userData));
        setFirebaseUser(fbUser);
        setUser(userData);
        setIsAuthenticated(true);
        console.log('🔐 ASCIN: Firebase user authenticated:', userData.email);
      } else {
        // User is signed out
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        setFirebaseUser(null);
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ ASCIN: Firebase login successful:', userCredential.user.email);
      return true;
    } catch (error: any) {
      console.error('❌ ASCIN: Firebase login error:', error.code, error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<boolean> => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ ASCIN: Google Sign-In successful:', result.user.email);
      return true;
    } catch (error: any) {
      console.error('❌ ASCIN: Google Sign-In error:', error.code, error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Update display name
      await updateProfile(userCredential.user, { displayName: name });
      console.log('✅ ASCIN: Firebase registration successful:', userCredential.user.email);
      return true;
    } catch (error: any) {
      console.error('❌ ASCIN: Firebase registration error:', error.code, error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
      setFirebaseUser(null);
      console.log('👋 ASCIN: User logged out');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getIdToken = async (): Promise<string | null> => {
    if (firebaseUser) {
      try {
        return await firebaseUser.getIdToken();
      } catch (error) {
        console.error('Error getting ID token:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      firebaseUser,
      login, 
      loginWithGoogle,
      register, 
      logout, 
      loading,
      getIdToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
